/**
 * QR Code Utilities for PeopleTag
 * Functions for generating QR codes for profile sharing
 */

/**
 * Generate QR code data URL for a profile link
 * Uses a simple text-based QR code generation (for production, consider using a library like qrcode.js)
 * @param {string} text - Text to encode in QR code
 * @param {number} size - QR code size in pixels
 * @returns {Promise<string>} Data URL of the QR code image
 */
export const generateQRCode = async (text, size = 200) => {
  // For a production app, you'd want to use a proper QR code library
  // This is a placeholder that creates a simple visual representation
  // In production, use: import QRCode from 'qrcode';
  // return await QRCode.toDataURL(text, { width: size });
  
  try {
    // Try to use a QR code library if available
    if (typeof window !== 'undefined' && window.QRCode) {
      return await window.QRCode.toDataURL(text, { width: size });
    }
    
    // Fallback: Create a simple canvas-based representation
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    // Simple placeholder pattern
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#000000';
    ctx.font = `${size / 10}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('QR Code', size / 2, size / 2 - 20);
    ctx.font = `${size / 20}px Arial`;
    ctx.fillText('(Install QR library)', size / 2, size / 2 + 10);
    
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw new Error('Failed to generate QR code');
  }
};

/**
 * Generate QR code for a profile
 * @param {string} username - Username
 * @param {string} baseUrl - Base URL of the application
 * @param {number} size - QR code size
 * @returns {Promise<string>} Data URL of the QR code
 */
export const generateProfileQRCode = async (username, baseUrl = window.location.origin, size = 200) => {
  const profileLink = `${baseUrl}/profile/${encodeURIComponent(username)}`;
  return await generateQRCode(profileLink, size);
};

/**
 * Download QR code as image
 * @param {string} dataUrl - QR code data URL
 * @param {string} filename - Filename for download
 */
export const downloadQRCode = (dataUrl, filename = 'qrcode.png') => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Display QR code in a modal or container
 * @param {string} dataUrl - QR code data URL
 * @param {HTMLElement} container - Container element to display QR code
 */
export const displayQRCode = (dataUrl, container) => {
  if (!container) return;
  
  const img = document.createElement('img');
  img.src = dataUrl;
  img.alt = 'QR Code';
  img.style.maxWidth = '100%';
  img.style.height = 'auto';
  
  container.innerHTML = '';
  container.appendChild(img);
};

